// ═══════════════════════════════════════════════════════════════════════
// Frame-time beacon decoder — pure logic, no DOM.
//
// The crash engine bakes a 26-cell data strip into every frame of the
// stream: [cell 0: START sentinel, always BRIGHT][cells 1..24: data bits,
// MSB first][cell 25: END sentinel, always DARK]. The 24-bit word is the
// race-relative frame time in centiseconds as a signed 20-bit
// two's-complement value followed by a 4-bit XOR-of-nibbles checksum.
// Decoding it from the playing <video>'s pixels locks the displayed
// multiplier to the exact frame each viewer is seeing (Evolution-style
// stream-synced UI). Reference codec: 155-game-crash
// `pipeline/frame_beacon.py`; spec: engine lockdown report §9.
//
// FAIRNESS: the payload is a frame clock and NOTHING else — it advances
// uniformly and leaks nothing about when the crash comes. Never add logic
// that anticipates the crash from it.
// ═══════════════════════════════════════════════════════════════════════

export const BEACON_CELL_COUNT = 26
export const BEACON_DATA_BITS = 24

// Sentinel-midpoint thresholding requires a minimum start−end contrast
// (0–255 scale). Post-encode reality is ~209 apart; 16 is the spec floor
// that also rejects beacon-less frames (betting/waiting footage).
export const MIN_SENTINEL_CONTRAST = 16

/**
 * Fractional geometry convention (spec §9.4): `x` is a fraction of frame
 * WIDTH; `y` and `cell` are fractions of frame HEIGHT (cells are square, so
 * a cell's horizontal extent is ALSO cell·H px). `count` is always 26.
 */
export type BeaconFractions = Readonly<{ x: number; y: number; cell: number; count: number }>

// Canonical fractions of the production 1080p bake (v1, bottom-LEFT:
// x=36/1920, y=1040/1080, cell=10/1080). Fractional by design so they
// resolve against ANY displayed/transcoded video size.
export const BEACON_FRACTIONS: BeaconFractions = {
  x: 0.01875,
  y: 0.962963,
  cell: 0.009259,
  count: BEACON_CELL_COUNT
}

// v2 placement (engine frame_beacon.py, owner ask 2026-07-20): bottom-RIGHT
// — the strip's flicker showed through the bottom-left stats overlay's
// glass; bottom-right is empty on every game surface. x=1624/1920 at the
// 1080p bake (the exact mirror of v1's inset), same row and cell size.
export const BEACON_FRACTIONS_RIGHT: BeaconFractions = {
  x: 0.845833,
  y: 0.962963,
  cell: 0.009259,
  count: BEACON_CELL_COUNT
}

// Sampling candidates, preferred order. BOTH corners stay live: clips
// prepared before the v2 move keep their bottom-left bake until re-prepared,
// and rounds alternate clips — the sampler adapts per clip (useFrameBeacon
// remembers which corner decoded last and tries it first).
export const BEACON_FRACTION_CANDIDATES: readonly BeaconFractions[] = [
  BEACON_FRACTIONS_RIGHT,
  BEACON_FRACTIONS
]

export type BeaconGeometry = Readonly<{
  /** First cell's LEFT edge, px — FLOAT. */
  x: number
  /** First cell's TOP edge, px — FLOAT. */
  y: number
  /** Cell size, px — FLOAT. NEVER round this and step by the integer: the
   * right-hand cells drift off the strip on downscaled video (spec §9.4). */
  cell: number
  count: number
}>

/** Resolve the fractional geometry against the *displayed* video size — all float. */
export const beaconGeometry = (
  videoWidth: number,
  videoHeight: number,
  fractions: BeaconFractions = BEACON_FRACTIONS
): BeaconGeometry => ({
  x: fractions.x * videoWidth,
  y: fractions.y * videoHeight,
  cell: fractions.cell * videoHeight,
  count: fractions.count
})

/** Cell i's centre: (X + (i + 0.5)·CELL, Y + 0.5·CELL) — float stepping from the left edge. */
export const cellCenter = (geom: BeaconGeometry, i: number): { cx: number; cy: number } => ({
  cx: geom.x + (i + 0.5) * geom.cell,
  cy: geom.y + 0.5 * geom.cell
})

/** XOR of the five 4-bit nibbles of the 20-bit value (spec §9.2). */
export const beaconChecksum = (value20: number): number =>
  (value20 ^ (value20 >> 4) ^ (value20 >> 8) ^ (value20 >> 12) ^ (value20 >> 16)) & 0xf

/**
 * Decode one frame's 26 cell samples (mean green/luma, 0–255 scale) into the
 * race-relative frame time in CENTISECONDS, or null when the frame carries
 * no decodable beacon. On null the caller keeps its last good lock — never
 * guess.
 *
 * Steps (spec §9.5): sentinel contrast check → threshold at the sentinel
 * midpoint (invariant to encode/transcode brightness shifts) → fold the 24
 * bits MSB-first → checksum → sign-extend.
 */
export const decodeBits = (samples: number[]): number | null => {
  if (samples.length !== BEACON_CELL_COUNT) return null
  const start = samples[0] ?? 0
  const end = samples[BEACON_CELL_COUNT - 1] ?? 0
  if (start - end < MIN_SENTINEL_CONTRAST) return null
  const threshold = (start + end) / 2
  let word = 0
  for (let i = 1; i <= BEACON_DATA_BITS; i++) {
    word = (word << 1) | ((samples[i] ?? 0) > threshold ? 1 : 0)
  }
  const value20 = word >>> 4
  if ((word & 0xf) !== beaconChecksum(value20)) return null
  // Sign-extend the 20-bit two's complement (negative during pre-roll).
  return value20 & 0x80000 ? value20 - 0x100000 : value20
}

/**
 * Average the green channel of the centre window of each of `count`
 * equal-width cells across an RGBA buffer (the beacon strip, resampled into
 * a small canvas). Window: s = min(4, floor(cell)) px, centred — cell
 * centres stepped in FLOAT per §9.4/§9.5.
 */
export const sampleCellGreens = (
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  count: number = BEACON_CELL_COUNT
): number[] => {
  const cellW = width / count
  const s = Math.max(1, Math.min(4, Math.floor(Math.min(cellW, height))))
  const out: number[] = new Array(count)
  const cy = height / 2
  const y0 = Math.min(height - s, Math.max(0, Math.round(cy - s / 2)))
  for (let i = 0; i < count; i++) {
    const cx = (i + 0.5) * cellW
    const x0 = Math.min(width - s, Math.max(0, Math.round(cx - s / 2)))
    let sum = 0
    for (let y = y0; y < y0 + s; y++) {
      const row = (y * width + x0) * 4
      for (let x = 0; x < s; x++) {
        sum += rgba[row + x * 4 + 1] ?? 0 // green channel
      }
    }
    out[i] = sum / (s * s)
  }
  return out
}

/**
 * Monotonic plausibility filter for successive decoded beacon times (both in
 * race-SECONDS). The frame clock only ever advances at ~1×, so:
 *  - forward jump  > 5s   → reject (null): garbled decode that passed checksum
 *  - backward jump > 0.5s → reject (null): new round / corrupt frame — re-lock fresh
 *  - backward jitter ≤ 0.5s → clamp to prev (a repeated/late frame; don't run backwards)
 * A `prev` of null (no fresh lock) accepts anything, including pre-roll negatives.
 */
export const acceptDecodedT = (prev: number | null, next: number): number | null => {
  if (prev === null) return next
  const delta = next - prev
  if (delta > 5) return null
  if (delta < -0.5) return null
  return delta < 0 ? prev : next
}

// ── Round-scope plausibility (bug a) ─────────────────────────────────────
// Frames can trail the event clock by a lot (elastic stream lag, measured
// 0.2–6.7s in production), but they can never LEAD it by more than sampling
// slack — a frame showing race time t exists only after the engine reached t.
//
// ⚠️ Applied to FRESH LOCKS ONLY since the 2026-08-17 clock-skew fix: the
// comparison runs on the VIEWER's wall clock, so a slow client clock makes
// every genuine frame look like it leads, and enforcing it per-sample froze
// the beacon for the whole round. Chained readings are guarded by
// CHAINED_FORWARD_SLACK_S + acceptDecodedT instead (see admitDecodedT). Do
// NOT "restore" this as a per-sample invariant — that recreates the incident.
export const BEACON_LEAD_SLACK_S = 1.0
// Sanity floor: a reading further than this BEHIND the event clock is not a
// lagged stream, it's garbage / wrong-round footage.
export const BEACON_LAG_FLOOR_S = 45
// With no race_started yet (betting phase) the current round can only be in
// pre-roll: negatives and small early positives are fine, anything past this
// is the previous round's tail still streaming.
export const BEACON_NO_START_MAX_T_S = 5

/**
 * Can a decoded beacon time belong to the CURRENT round? `eventClockT` is
 * seconds since this round's race_started event (undefined while the round
 * has no race_started yet). The observed failure this rejects: a new round's
 * race_started lands backend-side while the OLD round's tail is still
 * streaming — the decoder reads t≈45s two seconds into the new round and,
 * without this bound, drives the new round's display off old footage.
 *
 * An implausible reading must neither lock nor chain — drop it and wait for
 * the current round's frames to arrive.
 */
export const isPlausibleBeaconT = (beaconT: number, eventClockT: number | undefined): boolean => {
  if (eventClockT === undefined) return beaconT <= BEACON_NO_START_MAX_T_S
  return beaconT <= eventClockT + BEACON_LEAD_SLACK_S && beaconT > eventClockT - BEACON_LAG_FLOOR_S
}

/**
 * A CHAINED reading may only advance this far past its chain. Race time moves
 * at 1× wall time, so a legitimate chained advance is bounded by the chain's
 * own age (≤ CHAIN_MAX_AGE_MS = 2s in useFrameBeacon) plus decode slack —
 * `acceptDecodedT`'s +5s alone was calibrated when the wall-clock bound ALSO
 * ran per sample, and without it a checksum-colliding garble 1–5s ahead could
 * jump the lock (and stamp the crash early). This bound is tighter than the
 * old wall clock ever was, and skew-immune.
 */
export const CHAINED_FORWARD_SLACK_S = 2.5

// ── Consumer-side beacon freshness ladder ────────────────────────────────
// FRESH < CHAIN_MAX_AGE (useFrameBeacon) < DEAD — the ladder test pins the
// ordering. One definition each; every consumer imports from here (review
// 2026-08-17: two hand-copied FRESH constants and three unlinked 5000ms
// give-up constants were held in sync only by comments).

/** A reading younger than this is the frame on screen — extrapolate from it. */
export const BEACON_FRESH_MS = 1500

/**
 * A reading older than this is not "stale between 8Hz samples" — the decoder
 * has stopped producing readings mid-round (stall, decode failure, admission
 * rejection). Consumers give up on it: the display clock falls back to the
 * event clock (resolveDisplayTime), and the crash-stamp machinery's graces
 * (useVisualCrash CRASH_HOLD_GRACE_MS / crashHeroClamp
 * CRASH_STAMP_NO_M_GRACE_MS) are derived from this value. NOTE the graces run
 * from their own anchors (crash-event arrival), not from the reading's age —
 * deriving them keeps the POLICY single-sourced, not the origin.
 */
export const BEACON_DEAD_MS = 5000

export const isBeaconFresh = (b: { ageMs: number } | null): b is { t: number; ageMs: number } =>
  b !== null && b.ageMs < BEACON_FRESH_MS

export const isBeaconDead = (b: { ageMs: number } | null): boolean =>
  b !== null && b.ageMs >= BEACON_DEAD_MS

/**
 * Admit (or reject) one decoded reading — the whole per-sample decision.
 * THE canonical account of the 2026-08-17 incident and its guards lives here;
 * the call site and tests point back rather than retelling it.
 *
 * `eventClockT` is derived from the VIEWER's wall clock minus a SERVER
 * timestamp, so a machine whose clock runs slow makes every genuine frame look
 * like it LEADS the event clock. Applying the wall-clock branch of
 * `isPlausibleBeaconT` on every sample therefore rejected every reading, for
 * the whole round, silently and with no age escape hatch — the lock stopped
 * advancing and the display clock pinned (production 2026-08-17: the overlay
 * sat on "Tracking armed 3" for 30s, the bar stayed at the stake, and the
 * cash-out control never rendered).
 *
 * The admission rules, and why each is scoped the way it is:
 * - FRESH lock (`chained === null`): the full `isPlausibleBeaconT` bound
 *   applies — it is the only defense against the old round's still-streaming
 *   tail becoming this round's lock (`setBeaconRound` nulls the lock on a
 *   round change, so a tail must enter as a fresh lock).
 * - CHAINED + race_started known: the wall-clock bound is SKIPPED (the skew
 *   fix); the chained forward slack + `acceptDecodedT`'s backward bound guard
 *   continuity without any wall clock.
 * - CHAINED + no race_started yet: the ≤5s no-start bound never uses the wall
 *   clock, so skipping it buys no skew immunity — it only let a tail that
 *   fresh-locked at t≤5 during betting chain PAST 5s. It stays per-sample,
 *   EXCEPT for a chain whose origin was a pre-roll negative: that is the
 *   current round's own clip and may ride through a late race_started.
 */
export const admitDecodedT = (
  chained: number | null,
  next: number,
  eventClockT: number | undefined,
  chainOriginT: number | null = null
): number | null => {
  if (chained === null) {
    if (!isPlausibleBeaconT(next, eventClockT)) return null
    return acceptDecodedT(null, next)
  }
  if (next - chained > CHAINED_FORWARD_SLACK_S) return null
  if (
    eventClockT === undefined &&
    next > BEACON_NO_START_MAX_T_S &&
    (chainOriginT === null || chainOriginT >= 0)
  ) {
    return null
  }
  return acceptDecodedT(chained, next)
}

/**
 * Change 3 (beacon-armed cash-out) — the latch transition, run after each
 * ADMITTED, lock-updating decode. A frame showing race time t proves the
 * engine reached t, so beacon t ≥ reveal proves the round is climbing
 * regardless of the viewer's clock — but ONE admitted reading must never arm
 * a money control: after Change 3 the beacon is BOTH sides of the arming AND
 * (ceiling via the latch, floor via the display clock), so a garble that
 * slipped the chained bound would otherwise single-handedly arm the button.
 * Requiring two CONSECUTIVE admits kills that: a lone garble's successors are
 * all rejected (backward > 0.5s), so it never gets a second admit; rejections
 * reset the streak at the call site by passing atOrAbove=false back in.
 *
 * `hasRoundStart` (final verify 2026-08-17): before race_started is known the
 * no-start fresh gate legitimately admits t ≤ 5 — which includes the PREVIOUS
 * round's tail decoding t ∈ [3,5] during betting. Nothing may count toward
 * the streak until race_started exists, so beacon-arming always implies the
 * round is live client-side (the isClimbing conjunction's premise).
 */
export const climbProvenAfterAdmit = (
  prevAtOrAboveReveal: boolean,
  acceptedT: number,
  revealSeconds: number,
  hasRoundStart: boolean
): { atOrAbove: boolean; latched: boolean } => {
  if (!hasRoundStart) return { atOrAbove: false, latched: false }
  const atOrAbove = acceptedT >= revealSeconds
  return { atOrAbove, latched: prevAtOrAboveReveal && atOrAbove }
}
